import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import ReactGA from 'react-ga4';
import ReactMarkdown from 'react-markdown';
import '../../../assets/css/App.css';
import JobviousLogo from '../../../assets/Jobvious_logo@2x.svg';

class EstimateChatbot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      messages: [],
      inputValue: '',
      loading: true,
      sending: false,
      installerConfig: null,
      error: null,
      sessionId: 'estimate-session-' + Date.now(),
      quote: null,
      estimateId: null,
      campaignMetadata: null,
      campaignLoading: false,
      streamingText: '',       // Text being streamed in
      isStreaming: false,      // Whether currently receiving a stream
      currentTools: []         // Tools currently executing (for indicator)
    };

    this.messagesEndRef = React.createRef();
    this.mapRef = React.createRef();
    this.googleMap = null;
  }

  async componentDidMount() {
    ReactGA.initialize(process.env.REACT_APP_GA);
    ReactGA.send({ hitType: "pageview", page: "/estimate" });

    // Extract installer ID from URL path or query params
    const urlParams = new URLSearchParams(window.location.search);
    const pathMatch = window.location.pathname.match(/^\/estimate\/(.+)/);
    const slug = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
    let installerId = null;

    if (slug) {
      // Path param provided: numeric = direct ID, string = resolve via API
      if (/^\d+$/.test(slug)) {
        installerId = slug;
      } else {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/api/installer/resolve-slug/${encodeURIComponent(slug)}`);
          if (res.ok) {
            const data = await res.json();
            installerId = data.installer_id ? String(data.installer_id) : null;
          }
        } catch (e) {
          console.error('Slug resolution failed:', e);
        }
      }
    }

    // Fall back to query params if slug didn't resolve
    if (!installerId) {
      installerId = urlParams.get('installer') || urlParams.get('id');
    }

    // Parse URL parameters for attribution tracking
    // Detect platform from click IDs when utm_source not explicitly set
    const gclid = urlParams.get('gclid');
    const fbclid = urlParams.get('fbclid');

    this.initData = {
      referral_id: urlParams.get('rid') || urlParams.get('referral_id') || null,
      campaign_id: urlParams.get('cid') || urlParams.get('campaign_id') || null,
      customer_id: urlParams.get('customer_id') || null,
      utm_source: urlParams.get('utm_source')
        || (gclid ? 'google' : null)
        || (fbclid ? 'facebook' : null)
        || 'direct',
      utm_medium: urlParams.get('utm_medium')
        || (gclid ? 'cpc' : null)
        || (fbclid ? 'social' : null)
        || 'website',
      utm_campaign: urlParams.get('utm_campaign') || null,
      platform_click_id: gclid || fbclid || null,
      installer_id: installerId ? parseInt(installerId) : null
    };

    if (!installerId) {
      this.setState({
        loading: false,
        error: 'Invalid URL: No installer ID specified. Please use: /estimate?installer=123'
      });
      return;
    }

    this.installerId = parseInt(installerId);

    // Load installer configuration
    await this.loadInstallerConfig();

    // Load campaign metadata if campaign_id is present
    await this.loadCampaignMetadata();

    // Track campaign click if applicable
    await this.trackCampaignClick();

    // Start chatbot conversation
    await this.kickOff();
  }

  componentDidUpdate() {
    this.scrollToBottom();

    if (
      this.state.installerConfig?.locations?.length > 1 &&
      !this.googleMap &&
      this.mapRef.current
    ) {
      this.initMultiLocationMap();
    }
  }

  scrollToBottom = () => {
    // Scroll within the chat container only, not the entire page
    if (this.messagesEndRef.current) {
      const container = this.messagesEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  initMultiLocationMap() {
    if (!window.google?.maps) return;

    const locations = this.state.installerConfig.locations.filter(
      loc => loc.latitude && loc.longitude
    );

    if (locations.length <= 1) return;

    const map = new window.google.maps.Map(this.mapRef.current, {
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    const bounds = new window.google.maps.LatLngBounds();
    let openInfoWindow = null;

    locations.forEach((loc, idx) => {
      const position = { lat: parseFloat(loc.latitude), lng: parseFloat(loc.longitude) };
      bounds.extend(position);

      const marker = new window.google.maps.Marker({
        position,
        map,
        label: String.fromCharCode(65 + idx),
        title: loc.location_name
      });

      const directionsUrl = loc.google_place_id
        ? `https://www.google.com/maps/dir/?api=1&destination=place_id:${loc.google_place_id}`
        : `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`;

      let ratingHtml = '';
      if (loc.google_rating) {
        const fullStars = Math.floor(loc.google_rating);
        const halfStar = loc.google_rating - fullStars >= 0.3;
        const stars = '\u2605'.repeat(fullStars) + (halfStar ? '\u00BD' : '');
        ratingHtml = `<div style="margin:3px 0;font-size:13px">` +
          `<span style="color:#f4b400;letter-spacing:1px">${stars}</span> ` +
          `<span style="color:#666">${loc.google_rating}</span>` +
          (loc.google_review_count ? `<span style="color:#999;font-size:12px"> (${loc.google_review_count} reviews)</span>` : '') +
          `</div>`;
      }

      const businessName = this.state.installerConfig.name;

      const headerEl = document.createElement('span');
      headerEl.textContent = businessName;
      headerEl.style.cssText = 'font-size:15px;font-weight:bold';

      const infoWindow = new window.google.maps.InfoWindow({
        headerContent: headerEl,
        content: `<div style="min-width:180px">` +
          `<div style="color:#666;font-size:13px">${loc.location_name || 'Location'}` +
          (loc.address_city || loc.address_state ? ` · ${[loc.address_city, loc.address_state].filter(Boolean).join(', ')}` : '') +
          `</div>` +
          ratingHtml +
          `<a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" style="color:#1a73e8;text-decoration:none;font-size:13px">Get Directions</a>` +
          `</div>`
      });

      marker.addListener('click', () => {
        if (openInfoWindow) openInfoWindow.close();
        infoWindow.open(map, marker);
        openInfoWindow = infoWindow;
      });
    });

    map.fitBounds(bounds);
    this.googleMap = map;
  }

  getApiEndpoint() {
    return `${process.env.REACT_APP_API_URL}/chatbot/mcp`;
  }

  getStreamEndpoint() {
    return `${process.env.REACT_APP_API_URL}/chatbot/mcp/stream`;
  }

  getConfigEndpoint() {
    return `${process.env.REACT_APP_API_URL}/api/installer`;
  }

  async loadInstallerConfig() {
    try {
      const response = await fetch(`${this.getConfigEndpoint()}/${this.installerId}/config`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load installer configuration');
      }

      const config = await response.json();

      // Debug: Log the installer config to see what data we're getting
      console.log('=== INSTALLER CONFIG DEBUG ===');
      console.log('Full config:', config);
      console.log('Locations count:', config.locations?.length || 0);
      if (config.locations?.[0]) {
        const loc0 = config.locations[0];
        console.log('Primary location:', loc0.location_name, '→', loc0.address_city, loc0.address_state);
        console.log('Has latitude:', !!loc0.latitude, '→', loc0.latitude);
        console.log('Has longitude:', !!loc0.longitude, '→', loc0.longitude);
        console.log('Has google_place_id:', !!loc0.google_place_id, '→', loc0.google_place_id);
      }
      console.log('==============================');

      this.setState({ installerConfig: config });
    } catch (error) {
      console.error('Error loading installer config:', error);
      this.setState({
        error: 'Unable to load installer information: ' + error.message,
        loading: false
      });
    }
  }

  async loadCampaignMetadata() {
    // Only load if we have a campaign_id
    if (!this.initData.campaign_id) {
      return;
    }

    this.setState({ campaignLoading: true });

    try {
      const endpoint = `${process.env.REACT_APP_API_URL}/api/campaign/${this.initData.campaign_id}/metadata`;

      const response = await fetch(endpoint);

      if (response.ok) {
        const metadata = await response.json();
        console.log('Campaign metadata loaded:', metadata);
        this.setState({
          campaignMetadata: metadata,
          campaignLoading: false
        });
      } else {
        console.warn('Campaign metadata not found or inactive');
        this.setState({ campaignLoading: false });
      }
    } catch (error) {
      console.error('Error loading campaign metadata:', error);
      this.setState({ campaignLoading: false });
    }
  }

  async trackCampaignClick() {
    // Only track if we have campaign_id and customer_id, but NOT referral_id
    if (!this.initData.campaign_id || !this.initData.customer_id || this.initData.referral_id) {
      return;
    }

    try {
      const endpoint = `${process.env.REACT_APP_API_URL}/api/referral/campaign-click`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: this.initData.campaign_id,
          customer_id: this.initData.customer_id
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to track campaign click');
      }

      const data = await response.json();

      // Store the returned referral_id for use in chatbot session
      if (data.referral_id) {
        this.initData.referral_id = data.referral_id;
        console.log('Campaign click tracked. Referral ID:', data.referral_id);
      }
    } catch (error) {
      console.error('Error tracking campaign click:', error);
      // Don't block user experience if tracking fails
    }
  }

  /**
   * Parse SSE events from a text buffer
   * Server format: events separated by \n\n, each event is "data: {json}"
   * The type is inside the JSON as data.type
   * Returns { events: Array<{type, data}>, remaining: string }
   */
  parseSSEEvents(buffer) {
    const events = [];
    // Normalize line endings (handle \r\n and \r)
    const normalizedBuffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Split by double newline (event separator)
    const chunks = normalizedBuffer.split('\n\n');
    // Last chunk might be incomplete, keep it as remaining
    const remaining = chunks.pop() || '';

    for (const chunk of chunks) {
      if (!chunk.startsWith('data: ')) continue;

      try {
        const jsonStr = chunk.slice(6); // Remove "data: " prefix
        const data = JSON.parse(jsonStr);

        // Type is inside the JSON
        if (data.type) {
          events.push({ type: data.type, data });
        }
      } catch (e) {
        console.warn('[Stream] Failed to parse SSE chunk:', chunk, e);
      }
    }

    return { events, remaining };
  }

  /**
   * Send message using streaming endpoint
   * Falls back to standard endpoint on error
   */
  async sendMessageStreaming(message, isKickoff = false) {
    // Add placeholder bot message for streaming and capture its index
    let placeholderIndex;
    await new Promise(resolve => {
      this.setState(prevState => {
        placeholderIndex = prevState.messages.length;
        return {
          messages: [...prevState.messages, { text: '', type: 'bot', timestamp: new Date(), isStreaming: true }],
          isStreaming: true,
          streamingText: '',
          currentTools: []
        };
      }, resolve);
    });

    try {
      const payload = {
        message: message || '',
        sessionId: this.state.sessionId,
        channel: 'chat',
        installer_id: this.installerId
      };

      // Include initData only on kickoff
      if (isKickoff) {
        payload.initData = {
          ...this.initData,
          campaign_metadata: this.state.campaignMetadata
        };
      }

      const response = await fetch(this.getStreamEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      // Capture as const to avoid closure warnings in loop
      const msgIndex = placeholderIndex;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = this.parseSSEEvents(buffer);
        buffer = remaining;

        for (const event of events) {
          switch (event.type) {
            case 'thinking':
              // Server is processing - typing indicator is already shown
              break;

            case 'model':
              // Which model is being used (haiku/sonnet) - could display this if desired
              break;

            case 'delta':
              // Append text chunk to accumulated text
              if (event.data && event.data.text) {
                accumulatedText += event.data.text;
                // Capture current value to avoid closure issues
                const currentText = accumulatedText;
                // Update the streaming message in place
                this.setState(prevState => {
                  const messages = [...prevState.messages];
                  messages[msgIndex] = {
                    ...messages[msgIndex],
                    text: currentText
                  };
                  return { messages, streamingText: currentText };
                });
              }
              break;

            case 'tool_start':
              // Add tool to current tools list
              if (event.data && event.data.tool) {
                this.setState(prevState => ({
                  currentTools: [...prevState.currentTools, event.data.tool]
                }));
              }
              break;

            case 'tool_complete':
              // Remove tool from current tools list
              if (event.data && event.data.tool) {
                this.setState(prevState => ({
                  currentTools: prevState.currentTools.filter(t => t !== event.data.tool)
                }));
              }
              break;

            case 'done':
              // Finalize the message
              if (event.data) {
                // Update sessionId if provided
                if (event.data.sessionId) {
                  this.setState({ sessionId: event.data.sessionId });
                }
                // Update quote if provided
                if (event.data.quote) {
                  this.setState({ quote: event.data.quote });
                }
                // Update estimateId if provided
                if (event.data.estimateId) {
                  this.setState({ estimateId: event.data.estimateId });
                }
                // Use accumulated text for the final message
                const finalText = accumulatedText || '(no message)';
                this.setState(prevState => {
                  const messages = [...prevState.messages];
                  messages[msgIndex] = {
                    ...messages[msgIndex],
                    text: finalText,
                    isStreaming: false
                  };
                  return {
                    messages,
                    isStreaming: false,
                    streamingText: '',
                    currentTools: []
                  };
                });
              }
              break;

            case 'error':
              // Handle error event from stream
              throw new Error(event.data?.message || 'Stream error');

            default:
              // Ignore unknown event types
          }
        }
      }

      // If stream ended without a done event, finalize with accumulated text
      if (this.state.isStreaming) {
        this.setState(prevState => {
          const messages = [...prevState.messages];
          messages[placeholderIndex] = {
            ...messages[placeholderIndex],
            text: accumulatedText || '(no message)',
            isStreaming: false
          };
          return {
            messages,
            isStreaming: false,
            streamingText: '',
            currentTools: []
          };
        });
      }

      return true; // Success
    } catch (error) {
      // Streaming failed, will fall back to standard endpoint

      // Remove the placeholder message
      this.setState(prevState => ({
        messages: prevState.messages.filter((_, idx) => idx !== placeholderIndex),
        isStreaming: false,
        streamingText: '',
        currentTools: []
      }));

      // Fall back to standard endpoint
      return false;
    }
  }

  async kickOff() {
    this.setState({ loading: true });

    // Try streaming first
    const streamingSuccess = await this.sendMessageStreaming('', true);

    if (streamingSuccess) {
      this.setState({ loading: false });
      return;
    }

    // Fall back to standard endpoint
    try {
      const response = await fetch(this.getApiEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: '',
          sessionId: this.state.sessionId,
          channel: 'chat',
          installer_id: this.installerId,
          initData: {
            ...this.initData,
            campaign_metadata: this.state.campaignMetadata
          }
        })
      });

      const data = await response.json();

      if (response.ok) {
        this.addBotMessage(data.message || 'Hello! How can I help you today?');

        if (data.quote) {
          this.setState({ quote: data.quote });
        }

        this.setState({ loading: false });
      } else {
        this.addBotMessage('Sorry, I encountered an error starting the session.');
        this.setState({
          loading: false,
          error: data.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Kickoff error:', error);
      this.addBotMessage('Sorry, cannot reach the server right now.');
      this.setState({
        loading: false,
        error: 'Connection error: ' + error.message
      });
    }
  }

  addBotMessage(text) {
    this.setState(prevState => ({
      messages: [...prevState.messages, { text, type: 'bot', timestamp: new Date() }]
    }));
  }

  addUserMessage(text) {
    this.setState(prevState => ({
      messages: [...prevState.messages, { text, type: 'user', timestamp: new Date() }]
    }));
  }

  handleInputChange = (e) => {
    this.setState({ inputValue: e.target.value });
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  sendMessage = async () => {
    const message = this.state.inputValue.trim();
    if (!message || this.state.sending) return;

    this.addUserMessage(message);
    this.setState({ inputValue: '', sending: true });

    // Try streaming first
    const streamingSuccess = await this.sendMessageStreaming(message, false);

    if (streamingSuccess) {
      this.setState({ sending: false });
      return;
    }

    // Fall back to standard endpoint
    try {
      const response = await fetch(this.getApiEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          sessionId: this.state.sessionId,
          channel: 'chat',
          installer_id: this.installerId
        })
      });

      const data = await response.json();

      if (response.ok) {
        this.addBotMessage(data.message || '(no message)');

        if (data.quote) {
          this.setState({ quote: data.quote });
        }

        if (data.estimateId) {
          this.setState({ estimateId: data.estimateId });
        }
      } else {
        this.addBotMessage('Sorry, I encountered an error. Please try again.');
      }
    } catch (error) {
      console.error('Send error:', error);
      this.addBotMessage('Sorry, I cannot connect to the server right now. Please try again later.');
    } finally {
      this.setState({ sending: false });
    }
  };

  renderQuote() {
    const { quote } = this.state;
    if (!quote) return null;

    return (
      <div className="alert alert-warning my-3">
        <h6>Quote Context:</h6>
        {quote.vehicle && (
          <div>
            <strong>Vehicle:</strong> {[quote.vehicle.year, quote.vehicle.make, quote.vehicle.model].filter(Boolean).join(' ')}
            {quote.vehicle.sizeClass && <div><strong>Size Class:</strong> {quote.vehicle.sizeClass}</div>}
          </div>
        )}
        {quote.options && quote.options.length > 0 && (
          <div className="mt-2">
            <strong>Options:</strong>
            <ul className="mb-0 mt-1">
              {quote.options.map((option, idx) => (
                <li key={idx}>
                  {option.name || option.text || 'Option'}
                  {option.price != null && ` - $${Number(option.price).toLocaleString()}`}
                </li>
              ))}
            </ul>
            <em className="small">Say: first, second, or third.</em>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { messages, inputValue, loading, sending, installerConfig, error, estimateId, isStreaming } = this.state;
    const primaryLocation = installerConfig?.locations?.[0] || {};

    if (error && !installerConfig) {
      return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card shadow">
                  <div className="card-body text-center p-5">
                    <h3 className="text-danger mb-3">Error</h3>
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-vh-100 bg-light">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Header */}
              <div className="text-center mb-5">
                <p className="text-muted mb-1" style={{ fontSize: '1.4rem', fontWeight: '400' }}>
                  Schedule An Appointment With
                </p>
                <h1 className="display-4 mb-4" style={{ fontWeight: 'bold' }}>
                  {installerConfig?.name || 'Get Your Estimate'}
                </h1>

                {installerConfig && (
                  <>
                    {/* Contact Information Bar */}
                    <div className="d-flex flex-wrap justify-content-center mb-4">
                      <span className="text-muted mx-3">
                        <i className="fa-sharp fa-regular fa-map-marker mr-2"></i>
                        {installerConfig.locations?.length > 1
                          ? `${installerConfig.locations.length} Locations`
                          : [primaryLocation.address_city, primaryLocation.address_state].filter(Boolean).join(', ')
                        }
                      </span>
                      {installerConfig.phone && (
                        <span className="mx-3">
                          <i className="fa-sharp fa-regular fa-phone mr-2"></i>
                          <a href={`tel:${installerConfig.phone}`} className="text-decoration-none">
                            {installerConfig.phone}
                          </a>
                        </span>
                      )}
                      {installerConfig.email && (
                        <span className="mx-3">
                          <i className="fa-sharp fa-regular fa-envelope mr-2"></i>
                          <a href={`mailto:${installerConfig.email}`} className="text-decoration-none">
                            {installerConfig.email}
                          </a>
                        </span>
                      )}
                    </div>

                    <p className="lead text-muted mb-0">Get Your Personalized Quote in Minutes</p>
                  </>
                )}
              </div>

              {/* Campaign Offer Banner */}
              {this.state.campaignMetadata && this.state.campaignMetadata.campaign_description && (
                <div className="alert mb-4" style={{
                  backgroundColor: '#d4edda',
                  borderColor: '#c3e6cb',
                  borderRadius: '8px',
                  border: '2px solid #28a745'
                }}>
                  <div className="d-flex align-items-start">
                    <div className="mr-3">
                      <i className="fa-sharp fa-regular fa-gift fa-2x text-success"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-1" style={{ color: '#155724' }}>
                        <strong>Special Offer</strong>
                        {this.state.campaignMetadata.promo_code && (
                          <span className="badge badge-success ml-2" style={{ fontSize: '12px' }}>
                            {this.state.campaignMetadata.promo_code}
                          </span>
                        )}
                      </h5>
                      <p className="mb-1" style={{ color: '#155724' }}>
                        {this.state.campaignMetadata.campaign_description}
                      </p>
                      {this.state.campaignMetadata.discount_description && (
                        <p className="mb-0">
                          <strong style={{ color: '#155724' }}>
                            <i className="fa-sharp fa-regular fa-tag mr-1"></i>
                            {this.state.campaignMetadata.discount_description}
                          </strong>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Chat Container */}
              <div className="card shadow">
                <div className="card-body">
                  {/* Messages Area */}
                  <div
                    className="border rounded p-3 mb-3 bg-light"
                    style={{ height: '420px', overflowY: 'auto' }}
                  >
                    {loading && messages.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <div className="spinner-border mb-3" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <div>Initializing...</div>
                      </div>
                    ) : (
                      <>
                        {messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`mb-3 ${msg.type === 'user' ? 'text-right' : ''}`}
                          >
                            <div
                              className={`d-inline-block p-2 rounded ${
                                msg.type === 'user'
                                  ? 'bg-primary text-white'
                                  : 'bg-white border'
                              }`}
                              style={{ maxWidth: '80%', textAlign: 'left' }}
                            >
                              <strong>
                                {msg.type === 'user' ? 'You' : (installerConfig?.name || 'Assistant')}:
                              </strong>
                              <div className={`mt-1 ${msg.type === 'bot' ? 'chatbot-message' : ''}`}>
                                {msg.type === 'bot' ? (
                                  msg.isStreaming && !msg.text ? (
                                    // Show typing indicator while waiting for first text
                                    <div className="typing-indicator">
                                      <span></span>
                                      <span></span>
                                      <span></span>
                                    </div>
                                  ) : (
                                    // Show text (with streaming cursor if still streaming)
                                    <>
                                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                                      {msg.isStreaming && <span className="streaming-cursor">|</span>}
                                    </>
                                  )
                                ) : (
                                  msg.text
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {/* Typing indicator - only show when sending but not streaming */}
                        {sending && !isStreaming && (
                          <div className="mb-3">
                            <div
                              className="d-inline-block p-2 rounded bg-white border"
                              style={{ maxWidth: '80%', textAlign: 'left' }}
                            >
                              <strong>{installerConfig?.name || 'Assistant'}:</strong>
                              <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={this.messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Quote Display */}
                  {this.renderQuote()}

                  {/* Estimate ID Display */}
                  {estimateId && (
                    <div className="alert alert-success">
                      <strong>Estimate Created:</strong> ID {estimateId}
                    </div>
                  )}

                  {/* Input Area */}
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type your message here..."
                      value={inputValue}
                      onChange={this.handleInputChange}
                      onKeyPress={this.handleKeyPress}
                      disabled={loading || sending || isStreaming}
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-primary"
                        onClick={this.sendMessage}
                        disabled={loading || sending || isStreaming || !inputValue.trim()}
                      >
                        {sending || isStreaming ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Section */}
              {installerConfig && installerConfig.name && (
                installerConfig.locations?.length > 1 ? (
                  /* Multi-location: JS API map with pins */
                  <div className="card shadow mt-4">
                    <div className="card-header bg-white">
                      <h5 className="mb-0">
                        <i className="fa-sharp fa-regular fa-map-marker text-primary mr-2"></i>
                        Our Locations
                      </h5>
                    </div>
                    <div className="card-body p-0">
                      <div ref={this.mapRef} style={{ width: '100%', height: '450px' }} />
                    </div>
                    <div className="card-footer bg-white">
                      {installerConfig.locations
                        .filter(loc => loc.latitude && loc.longitude)
                        .map((loc, idx) => {
                          const dirUrl = loc.google_place_id
                            ? `https://www.google.com/maps/dir/?api=1&destination=place_id:${loc.google_place_id}`
                            : `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`;
                          return (
                            <div key={idx} className="d-flex align-items-center justify-content-between py-2" style={idx > 0 ? { borderTop: '1px solid #eee' } : {}}>
                              <div>
                                <strong className="mr-2" style={{ color: '#1a73e8' }}>{String.fromCharCode(65 + idx)}</strong>
                                <span>{loc.location_name || 'Location'}</span>
                                <span className="text-muted ml-2 small">
                                  {[loc.address_city, loc.address_state].filter(Boolean).join(', ')}
                                </span>
                                {loc.google_rating && (
                                  <span className="ml-2 small">
                                    <span style={{ color: '#f4b400' }}>&#9733;</span>{' '}
                                    {loc.google_rating}
                                    {loc.google_review_count && (
                                      <span className="text-muted"> ({loc.google_review_count})</span>
                                    )}
                                  </span>
                                )}
                              </div>
                              <a
                                href={dirUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fa-sharp fa-regular fa-diamond-turn-right mr-1"></i>
                                Directions
                              </a>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                ) : (
                  /* Single location: existing iframe */
                  <div className="card shadow mt-4">
                    <div className="card-header bg-white">
                      <h5 className="mb-0">
                        <i className="fa-sharp fa-regular fa-map-marker text-primary mr-2"></i>
                        Our Location
                      </h5>
                    </div>
                    <div className="card-body p-0">
                      <iframe
                        width="100%"
                        height="450"
                        frameBorder="0"
                        style={{ border: 0 }}
                        src={primaryLocation.google_place_id
                          ? `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_API_KEY}&q=place_id:${primaryLocation.google_place_id}`
                          : `https://maps.google.com/maps?q=${primaryLocation.latitude},${primaryLocation.longitude}&z=15&output=embed`
                        }
                        allowFullScreen
                        title={`${installerConfig.name} Location`}
                        loading="lazy"
                      />
                    </div>
                    <div className="card-footer bg-white text-center">
                      <a
                        href={primaryLocation.google_place_id
                          ? `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${primaryLocation.google_place_id}`
                          : `https://www.google.com/maps/dir/?api=1&destination=${primaryLocation.latitude},${primaryLocation.longitude}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        <i className="fa-sharp fa-regular fa-external-link mr-1"></i>
                        View on Google Maps
                      </a>
                    </div>
                  </div>
                )
              )}

              {/* Powered by Jobvious Footer */}
              <div className="text-center mt-4 py-3">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-muted">Powered by</span>
                  <img
                    src={JobviousLogo}
                    alt="Jobvious"
                    style={{ height: '30px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(EstimateChatbot);
